import React, { useState, useMemo } from 'react';
import { 
  Sliders, 
  HardDrive, 
  Info, 
  ShieldCheck, 
  Activity, 
  TrendingUp, 
  Coins, 
  ChevronRight, 
  HelpCircle,
  Database,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { motion } from 'motion/react';

interface StorageCalculatorProps {
  initialPlan?: 'solo' | 'growth' | 'agency';
}

export const StorageCalculator: React.FC<StorageCalculatorProps> = ({ initialPlan = 'solo' }) => {
  // Silders and interactive variables
  const [imagesCount, setImagesCount] = useState<number>(25);
  const [videosCount, setVideosCount] = useState<number>(3);
  const [refinementsFactor, setRefinementsFactor] = useState<number>(30); // in percent (extra draft storage)
  const [monthlyViews, setMonthlyViews] = useState<number>(100); // estimated views of shared links
  const [retentionMonths, setRetentionMonths] = useState<number>(12); // archive retention
  
  // Currency options matching our daily list from rules
  const [currency, setCurrency] = useState<'USD' | 'ZAR' | 'BRL' | 'GBP'>('USD');

  const currencies = {
    USD: { symbol: '$', rate: 1, label: 'USD' },
    ZAR: { symbol: 'R', rate: 18.2, label: 'ZAR' },
    BRL: { symbol: 'R$', rate: 5.4, label: 'BRL' },
    GBP: { symbol: '£', rate: 0.8, label: 'GBP' }
  };

  const curr = currencies[currency];

  // Constants
  const IMAGE_SIZE_MB = 1.5;
  const VIDEO_SIZE_MB = 8.0;
  const STORAGE_PRICE_PER_GB_MO = 0.026; // Standard cloud storage block pricing
  const EGRESS_PRICE_PER_GB = 0.12;       // Inbound/outbound egress delivery pricing

  // Dynamic calculations
  const metrics = useMemo(() => {
    // Basic single month content output
    const rawImagesMB = imagesCount * IMAGE_SIZE_MB;
    const rawVideosMB = videosCount * VIDEO_SIZE_MB;
    const singleMonthBaseMB = rawImagesMB + rawVideosMB;
    
    // Total drafts & refinements factor
    // Generates extra size for saved revisions and feedback items
    const totalRefinedMB = singleMonthBaseMB * (1 + refinementsFactor / 100);
    
    // Total active monthly output (including drafts) in GB
    const monthlyOutputGB = totalRefinedMB / 1024;
    
    // Outbound Bandwidth: Every view of shared link loads the active media assets
    const estimatedActiveLinkSizeGB = (rawImagesMB + (rawVideosMB * 0.4)) / 1024; // assumes video thumbnails or optimized preview are mostly loaded first
    const monthlyEgressGB = estimatedActiveLinkSizeGB * monthlyViews;
    
    // Total active yearly accumulated disk storage (based on retention model)
    const accumulatedStorageGB = (totalRefinedMB * Math.min(12, retentionMonths)) / 1024;

    // Hard server infrastructure costs
    const monthlyStorageHardCostUSD = accumulatedStorageGB * STORAGE_PRICE_PER_GB_MO;
    const monthlyEgressHardCostUSD = monthlyEgressGB * EGRESS_PRICE_PER_GB;
    const totalHardCostUSD = monthlyStorageHardCostUSD + monthlyEgressHardCostUSD;

    // Convert to currency
    const finalHardCost = totalHardCostUSD * curr.rate;

    // Find recommended plans based on these parameters
    let recommendedTier = 'Solo';
    let tierColor = 'text-indigo-600 bg-indigo-50 border-indigo-200';
    const totalPostsPerMonth = imagesCount + videosCount;

    if (totalPostsPerMonth > 30 || videosCount > 3 || accumulatedStorageGB > 1.5) {
      recommendedTier = 'Growth';
      tierColor = 'text-emerald-600 bg-emerald-50 border-emerald-200';
    }
    if (totalPostsPerMonth > 80 || videosCount > 10 || accumulatedStorageGB > 5.0 || monthlyViews > 1000) {
      recommendedTier = 'Agency';
      tierColor = 'text-violet-600 bg-violet-50 border-violet-200';
    }

    return {
      monthlyOutputMB: Math.round(totalRefinedMB),
      monthlyEgressGB: parseFloat(monthlyEgressGB.toFixed(2)),
      accumulatedStorageGB: parseFloat(accumulatedStorageGB.toFixed(2)),
      hardCost: finalHardCost,
      recommendedTier,
      tierColor
    };
  }, [imagesCount, videosCount, refinementsFactor, monthlyViews, retentionMonths, curr]);

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden p-6 md:p-8">
      {/* Title & Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-6 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600">
              <Database className="w-5 h-5" />
            </span>
            <span className="text-xs font-bold text-indigo-600 tracking-wider uppercase">Advanced Tool</span>
          </div>
          <h3 className="text-2xl font-black text-gray-900 tracking-tight">Cloud Storage Cost Estimator</h3>
          <p className="text-sm text-gray-500 mt-1">
            Calculate your active multi-tenant media footprint and understand how system tiers are costed.
          </p>
        </div>
        
        {/* Currency Toggle */}
        <div className="flex bg-gray-100 p-1 rounded-xl self-start md:self-center">
          {(['USD', 'ZAR', 'BRL', 'GBP'] as const).map(c => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all uppercase ${
                currency === c 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Controls Section */}
        <div className="lg:col-span-7 space-y-6">
          <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5 pb-2 border-b border-gray-50">
            <Sliders className="w-4 h-4 text-indigo-500" />
            Adjust Volume Parameters
          </h4>

          {/* Slider 1: Image posts */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm font-bold text-gray-700">
              <span className="flex items-center gap-1.5">
                Images Generated per month
                <span className="text-xs font-normal text-gray-400">(~{IMAGE_SIZE_MB}MB ea.)</span>
              </span>
              <span className="text-indigo-600 font-extrabold bg-indigo-50 px-2.5 py-1 rounded-lg">
                {imagesCount}
              </span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={imagesCount} 
              onChange={e => setImagesCount(parseInt(e.target.value))}
              className="w-full accent-indigo-600 h-2 bg-gray-100 rounded-lg cursor-pointer"
            />
            <div className="flex justify-between text-[10px] font-semibold text-gray-400">
              <span>0 posts</span>
              <span>100 posts</span>
            </div>
          </div>

          {/* Slider 2: Video posts */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm font-bold text-gray-700">
              <span className="flex items-center gap-1.5">
                Videos / Reels per month
                <span className="text-xs font-normal text-gray-400">(~{VIDEO_SIZE_MB}MB ea.)</span>
              </span>
              <span className="text-orange-600 font-extrabold bg-orange-50 px-2.5 py-1 rounded-lg">
                {videosCount}
              </span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="30" 
              value={videosCount} 
              onChange={e => setVideosCount(parseInt(e.target.value))}
              className="w-full accent-orange-600 h-2 bg-gray-100 rounded-lg cursor-pointer"
            />
            <div className="flex justify-between text-[10px] font-semibold text-gray-400">
              <span>0 videos (Solo included block: 3)</span>
              <span>30 videos</span>
            </div>
          </div>

          {/* Slider 3: Work Refinement & Feedback */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm font-bold text-gray-700">
              <span>
                Refinement & Drafting Rate
                <span className="text-xs font-normal text-gray-400 ml-1.5">(Intermediate modifications footprint)</span>
              </span>
              <span className="text-pink-600 font-extrabold bg-pink-50 px-2.5 py-1 rounded-lg">
                +{refinementsFactor}%
              </span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="150" 
              value={refinementsFactor} 
              onChange={e => setRefinementsFactor(parseInt(e.target.value))}
              className="w-full accent-pink-500 h-2 bg-gray-100 rounded-lg cursor-pointer"
            />
            <div className="flex justify-between text-[10px] font-semibold text-gray-400">
              <span>0% (Clean saves)</span>
              <span>150% (High revision count)</span>
            </div>
          </div>

          {/* Slider 4: Monthly Views */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm font-bold text-gray-700">
              <span className="flex items-center gap-1.5">
                Shared Link Client Views
                <span className="text-xs font-normal text-gray-400">(Calculates Egress egress delivery cost)</span>
              </span>
              <span className="text-teal-600 font-extrabold bg-teal-50 px-2.5 py-1 rounded-lg">
                {monthlyViews.toLocaleString()} views
              </span>
            </div>
            <input 
              type="range" 
              min="10" 
              max="3000" 
              step="10"
              value={monthlyViews} 
              onChange={e => setMonthlyViews(parseInt(e.target.value))}
              className="w-full accent-teal-600 h-2 bg-gray-100 rounded-lg cursor-pointer"
            />
            <div className="flex justify-between text-[10px] font-semibold text-gray-400">
              <span>10 views</span>
              <span>3,000 views</span>
            </div>
          </div>

          {/* Archive Retention */}
          <div className="p-4 bg-gray-50 border border-gray-100/60 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-xs font-bold text-gray-700">Archive Retention Period</span>
              <p className="text-[11px] text-gray-400">We store old generated monthly assets securely</p>
            </div>
            <select
              value={retentionMonths}
              onChange={e => setRetentionMonths(parseInt(e.target.value))}
              className="text-xs border border-gray-200 bg-white rounded-xl px-3 py-2 text-gray-900 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer min-w-[150px]"
            >
              <option value={1}>1 Month History</option>
              <option value={3}>3 Months History</option>
              <option value={6}>6 Months History</option>
              <option value={12}>12 Months History</option>
              <option value={24}>24 Months History</option>
            </select>
          </div>
        </div>

        {/* Output Section */}
        <div className="lg:col-span-5 bg-slate-50 border border-slate-100/80 rounded-3xl p-6 flex flex-col justify-between">
          <div className="space-y-6">
            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5 pb-2 border-b border-gray-200">
              <Activity className="w-4 h-4 text-indigo-500" />
              Dynamic Platform Footprint
            </h4>

            {/* Metric Blocks */}
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-gray-100">
                <span className="text-xs font-bold text-gray-500">Monthly Output Size</span>
                <span className="text-xs font-extrabold text-indigo-900">{metrics.monthlyOutputMB} MB</span>
              </div>
              <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-gray-100">
                <span className="text-xs font-bold text-gray-500">Peak Storage Footprint</span>
                <span className="text-xs font-extrabold text-pink-700">{metrics.accumulatedStorageGB} GB</span>
              </div>
              <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-gray-100">
                <span className="text-xs font-bold text-gray-500">Client Outbound Egress</span>
                <span className="text-xs font-extrabold text-teal-700">{metrics.monthlyEgressGB} GB/mo</span>
              </div>
            </div>

            {/* Calculations Breakdown info */}
            <div className="p-4 bg-indigo-50/50 border border-indigo-100/40 rounded-2xl">
              <div className="flex items-start gap-2.5">
                <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                <div className="text-[11px] leading-normal text-indigo-950">
                  <span className="font-bold">Did you know?</span> Storage hosting itself is inexpensive (~{curr.symbol}{(STORAGE_PRICE_PER_GB_MO * curr.rate).toFixed(3)}/GB). The main host cost is delivery bandwidth egress (~{curr.symbol}{(EGRESS_PRICE_PER_GB * curr.rate).toFixed(2)}/GB) whenever your shared content links are viewed or downloaded by clients.
                </div>
              </div>
            </div>

            {/* Hard Cost Estimation */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Estimated Infrastructure Hard Cost</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-black text-gray-900">
                  {curr.symbol}{metrics.hardCost.toFixed(2)}
                </span>
                <span className="text-xs text-gray-400 font-bold">/ month</span>
              </div>
              <p className="text-[10px] text-gray-400/90 leading-tight mt-1.5">
                This covers actual database lookups, file systems hosting, and CDN delivery for this usage configuration.
              </p>
            </div>
          </div>

          {/* Recommended Plan matching */}
          <div className="pt-6 border-t border-gray-200/60 mt-6">
            <div className="flex items-center justify-between p-3.5 bg-white border border-gray-100 rounded-2xl">
              <div>
                <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Recommended Plan</span>
                <p className="text-lg font-black text-gray-900 mt-0.5">{metrics.recommendedTier} Plan</p>
              </div>
              <div className={`px-3.5 py-1.5 rounded-full border text-xs font-extrabold ${metrics.tierColor}`}>
                Ideal Match
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
